"use client";

import React, { useEffect, useState, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import FilterList from './FilterList';
import ProductGrid from './ProductGridClient';
import ProductCard from '../Components/produit/product_card';
import Header from '../Components/header';
import WaveHeader from '../Components/home/WaveHeader';
import TopHeader from '@/app/Components/TopHeader';
import Footer from '../Components/footer';
import { API_BASE_URL } from '@/app/lib/api';
import loggedFetch from '@/lib/logFetch';

type Product = any;

function ProductsInfiniteContent() {
  const searchParams = useSearchParams();
  const searchQuery = searchParams?.get('q') || '';

  const [products, setProducts] = useState<Product[]>([]);
  const [skip, setSkip] = useState(0);
  const [take] = useState(24);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const currentFilters = useRef<any>({});
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const didInitialLoad = useRef(false);

  const buildQuery = (filters: any, s: number, t: number) => {
    const params = new URLSearchParams();
    params.set('skip', String(s));
    params.set('take', String(t));

    if (filters) {
      if (Array.isArray(filters.categories) && filters.categories.length > 0) {
        params.set('categoryId', filters.categories[0]);
      }
      if (Array.isArray(filters.gammeSlugs) && filters.gammeSlugs.length > 0) {
        params.set('gammeSlug', filters.gammeSlugs.join(','));
      }

      const attrs = filters.attributes || {};
      const allVals: string[] = [];
      for (const [k, vals] of Object.entries(attrs)) {
        if (!Array.isArray(vals)) continue;
        const clean = (vals as string[]).filter(Boolean);
        if (clean.length === 0) continue;
        allVals.push(...clean);
        params.set(`attr_${k}`, clean.join(','));
      }
      if (allVals.length > 0) params.set('attributeValueIds', Array.from(new Set(allVals)).join(','));
      if (allVals.length > 0) params.set('applyCategoryFilter', 'false');
    }

    return params.toString();
  };

  const fetchPage = React.useCallback(async (s: number, replace = false, filters?: any, sort?: string) => {
    if ((fetchPage as any)._inFlight) return;
    (fetchPage as any)._inFlight = true;
    setLoading(true);
    try {
      let res;
      if (searchQuery) {
        res = await loggedFetch(`${API_BASE_URL}/products/search?q=${encodeURIComponent(searchQuery)}`, { cache: 'no-store' });
      } else {
        const q = buildQuery(filters || currentFilters.current, s, take) + (sort ? `&sort=${encodeURIComponent(sort)}` : '');
        res = await loggedFetch(`${API_BASE_URL}/categories/products/all?${q}`, { cache: 'no-store' });
      }

      if (!res.ok) {
        console.warn('products fetch failed', res.status);
        return;
      }
      const data = await res.json();
      const items = Array.isArray(data) ? data : (data.products || data.items || []);
      if (replace) setProducts(items);
      else setProducts((p) => [...p, ...items]);
      
      if (searchQuery) {
        setHasMore(false);
      } else {
        setHasMore((items?.length || 0) === take);
        setSkip((prev) => prev + (items?.length || 0));
      }
    } catch (e) {
      console.error('fetch products error', e);
    } finally {
      (fetchPage as any)._inFlight = false;
      setLoading(false);
    }
  }, [take, searchQuery]);

  const [sort, setSort] = useState<string>('');

  // refetch when sort changes
  useEffect(() => {
    setSkip(0);
    if (!didInitialLoad.current) return;
    setSkip(0);
    fetchPage(0, true, undefined, sort);
  }, [sort, fetchPage]);

  // Initial load / search parameter change load
  useEffect(() => {
    setSkip(0);
    fetchPage(0, true, undefined, sort).then(() => {
      didInitialLoad.current = true;
    }).catch(() => {
      didInitialLoad.current = true;
    });
  }, [searchQuery, fetchPage]);

  useEffect(() => {
    // listen for filter changes
    const handler = (e: any) => {
      const detail = e?.detail || e;
      currentFilters.current = detail || {};
      setSkip(0);
      fetchPage(0, true, detail, sort);
    };
    window.addEventListener('filters-changed', handler as EventListener);

    return () => {
      window.removeEventListener('filters-changed', handler as EventListener);
    };
  }, [fetchPage, sort]);

  // IntersectionObserver to implement infinite scroll (disabled during search)
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || searchQuery) return;

    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && hasMore && !loading) {
            fetchPage(skip, false, undefined, sort);
          }
        }
      },
      { root: null, rootMargin: '300px', threshold: 0.1 }
    );

    observerRef.current.observe(el);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasMore, loading, skip, fetchPage, sort, searchQuery]);

  const normalizeProduct = (p: any) => {
    const id = p.id ?? p._id ?? String(p.id ?? p._id ?? Math.random());
    const name = p.name ?? p.title ?? `Produit ${id}`;
    const slug = p.slug ?? p.handle ?? String(id);
    const images = Array.isArray(p.images) ? p.images : (p.image ? [p.image] : []);
    const price = typeof p.price === 'string' ? parseFloat(p.price.replace(/,/g, '.')) : (typeof p.price === 'number' ? p.price : 0);
    const promoPrice = typeof p.promoPrice === 'string' ? parseFloat(p.promoPrice.replace(/,/g, '.')) : (typeof p.promoPrice === 'number' ? p.promoPrice : undefined);
    const stock = typeof p.stock === 'number' ? p.stock : (typeof p.quantity === 'number' ? p.quantity : (p.stock == null ? undefined : Number(p.stock)));
    const category = p.category ? (typeof p.category === 'string' ? { id: '', name: p.category } : { id: p.category.id ?? '', name: p.category.name ?? '' }) : { id: '', name: '' };
    return { id, name, slug, images, price, promoPrice, stock, category, ...p };
  };

  return (
    <>
      <Header />
      <WaveHeader
        title={searchQuery ? `Recherche : "${searchQuery}"` : "Nos produits"}
        subtitle={searchQuery ? `${products.length} produit(s) trouvé(s)` : "Découvrez notre catalogue"}
      />

      <div className="container max-w-8xl mx-auto px-2 py-8 bg-gray-50">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Sidebar: Filters */}
          <aside className="w-full lg:w-1/4 flex-shrink-0">
            <div className="lg:sticky lg:top-42">
              <FilterList />
            </div>
          </aside>

          {/* Main Content: Products */}
          <main className="w-full lg:w-3/4">
            
            {searchQuery && (
              <div className="mb-6 flex items-center justify-between bg-[#ab1e8f]/5 border border-[#ab1e8f]/10 rounded-2xl p-4">
                <p className="text-sm text-slate-700">
                  Affichage des résultats pour "<span className="font-semibold text-[#ab1e8f]">{searchQuery}</span>"
                </p>
                <Link href="/nos-produits" className="text-xs font-bold text-[#ab1e8f] hover:underline flex items-center gap-1">
                  Effacer la recherche ✕
                </Link>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.length === 0 && !loading && (
                <div className="col-span-full py-8 text-center text-gray-500">
                  Aucun produit
                </div>
              )}

              {products.map((p, idx) => {
                const np = normalizeProduct(p);
                return (
                  <div key={np.id}>
                    <ProductCard product={np} priority={idx === 0} />
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col items-center">
              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="w-full h-2" />

              {/* Status message */}
              {loading && (
                <p className="text-gray-600 text-sm mt-2">Chargement...</p>
              )}

              {!hasMore && products.length > 0 && (
                <p className="text-gray-400 text-sm mt-2">Vous avez tout vu !</p>
              )}
            </div>
          </main>

        </div>
      </div>
      <Footer />
    </>
  );
}

export default function ProductsInfiniteClient() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600 text-sm animate-pulse">Chargement...</p>
      </div>
    }>
      <ProductsInfiniteContent />
    </Suspense>
  );
}
